import { Paper } from "helpers/api";
import React, { useEffect, useRef, useState } from "react";
import BeeswarmPaperPlot from "components/charts/BeeswarmPaperPlot";
import { groupPapersByTimePeriod } from "components/charts/utils/timeline/timeline";
import { Info } from "lucide-react";
import { Text } from "design_system";

/**
 * Props for the ResultsTimeline component
 */
interface ResultsTimelineProps {
  /** Array of papers used in analysis */
  papers?: Paper[];
  /** Whether the analysis is for Deep Research */
  isProResearch?: boolean;
  /** Callback function triggered when a user clicks on a citation */
  onClickCitation?: (
    paper: Paper,
    paperIndex: number,
    referenceLineNumber: number,
    associatedSentence: string
  ) => void;
  /** Search ID for Consensus Meter coloring in citations */
  searchId?: string;
  /** Whether to show the Consensus Meter coloring in citations */
  showCitationMeterResult?: boolean;
}

const ResultsTimeline: React.FC<ResultsTimelineProps> = ({
  papers = [],
  isProResearch = false,
  onClickCitation,
  searchId,
  showCitationMeterResult,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(768);
  const timelineHeight = papers.length > 20 ? 350 : 250;
  const paperCap = isProResearch ? 50 : 20;

  // Needed so we don't plot papers beyond the first page
  const truncatedPaperList = papers.slice(0, paperCap);

  // Resize observer to track container width changes
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        setContainerWidth(width);
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <div ref={containerRef} className="w-full max-w-page">
      <BeeswarmPaperPlot
        papers={truncatedPaperList}
        onClickCitation={onClickCitation}
        searchId={searchId}
        showCitationMeterResult={showCitationMeterResult}
        width={containerWidth}
        height={timelineHeight}
      />
    </div>
  );
};

export const ResultsTimelinePlaceholder = ({
  state = "loading",
}: {
  state?: "loading" | "empty";
}) => {
  const displayText =
    state === "loading" ? "Loading timeline..." : "No papers to display";
  return (
    <div className="flex items-center justify-center gap-2 p-6 w-full bg-bg-faint text-fg-muted">
      {state === "empty" && <Info size={20} />}
      <Text size="sm" theme="muted" className="m-0!">
        {displayText}
      </Text>
    </div>
  );
};

export const ResultsTimelineMarkdown = ({
  papers,
  isProResearch = false,
}: ResultsTimelineProps) => {
  if (!papers || !papers.length) {
    return "No papers to display";
  }

  const paperCap = isProResearch ? 50 : 20;
  const truncatedPaperList = papers.slice(0, paperCap);

  // Group papers by time periods using shared logic
  const groupedPapers = groupPapersByTimePeriod(truncatedPaperList);

  // Convert Map to sorted array for consistent ordering
  const sortedTimePeriods = Array.from(groupedPapers.entries()).sort(
    ([a], [b]) => {
      // Sort by time period (year or month-year)
      const isYearly = !a.includes(" ");
      const isYearlyB = !b.includes(" ");

      if (isYearly && isYearlyB) {
        return parseInt(a) - parseInt(b);
      } else if (isYearly) {
        return -1; // Years come before months
      } else if (isYearlyB) {
        return 1; // Months come after years
      } else {
        // Both are monthly, sort by date
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
      }
    }
  );

  // Generate markdown string
  const markdownLines: string[] = [];

  sortedTimePeriods.forEach(([timePeriod, papersInPeriod]) => {
    // Sort papers by original index (smallest to largest)
    const sortedPapers = papersInPeriod
      .map((paper) => ({
        paper,
        originalIndex: papers.findIndex((p) => p.paper_id === paper.paper_id),
      }))
      .sort((a, b) => a.originalIndex - b.originalIndex);

    const paperCount = sortedPapers.length;
    const citationText = sortedPapers
      .map(({ originalIndex }) => `[${originalIndex + 1}]`)
      .join("");

    markdownLines.push(`- **${timePeriod}**`);
    markdownLines.push(
      `  - ${paperCount} paper${paperCount !== 1 ? "s" : ""}: ${citationText}`
    );
  });

  return markdownLines.join("\n");
};

export default ResultsTimeline;
